import { notification } from "antd";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import GlobalLoading from "components/GlobalLoading";
import { Header, Content, Footer, Layout } from "components/Layout";
import {
	fetchFormDefinitionById,
	postRequestFormComplete,
	selectFormError,
	errorEdited as formErrorEdited,
	selectFormStatus,
	fetchCmeFormById,
} from "reducers/formSlice";
import {
	loadingEdited,
	screenTitleEdited,
	selectGlobal,
} from "reducers/global";
import { fetchTreeByDomain } from "reducers/treesSlice";
import { CustomButton } from "components/Paginator";
import { COORDINATOR, useSecurity } from "features/cme/Components/security";
import Leave from "../leaveLine";
import { validateAllLeaveLines } from "../leaveLine/leaveLineSlice";

import { selectRequestFormData } from "../requestForm/requestFormSlice";
import {
	errorEdited,
	postProcessLeaveRequest,
	selectAdminError,
	selectAdminStatus,
} from "./adminSlice";
import { selectUserOrganisation } from "reducers/securitySlice";

const LeaveClaim = () => {
	useSecurity(COORDINATOR);
	const { formDefinitionId, formId, formTypeId } = useParams();
	const dispatch = useDispatch();
	const domain = useSelector(selectUserOrganisation);
	const { loading } = useSelector(selectGlobal);

	//reset store every time a new form or form def is loaded
	useEffect(() => {
		dispatch({ type: "RESET_CME" });
		//while we are loading forms/defs etc, we set the global loading
		dispatch(loadingEdited(true));
	}, [dispatch, formId, formDefinitionId]);

	//first retrieve form definition
	useEffect(() => {
		const fetchData = async () => {
			dispatch(fetchFormDefinitionById(formDefinitionId));
		};

		const fetchFormDefinition = async () => {
			if (formDefinitionId && domain) {
				await fetchData();
				dispatch(fetchTreeByDomain({ domain }));
			}
		};

		fetchFormDefinition();
	}, [dispatch, formDefinitionId, domain]);

	useEffect(() => {
		const fetchAll = async () => {
			//load form
			if (formId) {
				dispatch(fetchCmeFormById(formId));
			}
		};

		const setup = async () => {
			await fetchAll();
			//finally, we remove loading
			dispatch(loadingEdited(false));
		};

		setup();
	}, [dispatch, formDefinitionId, formId, formTypeId]);

	const history = useHistory();
	//get the parent form id of this form - this is what we need to call processLeaveRequest
	const { parentFormId, statusId } = useSelector(selectRequestFormData);
	const onSubmit = async () => {
		//validate
		const errors = await dispatch(validateAllLeaveLines()).unwrap();

		if (errors === 0) {
			//1. save requestForm in its entirety
			await dispatch(
				postRequestFormComplete({
					options: { isSilentSave: true },
				})
			).unwrap();
			//2. process leave request
			await dispatch(
				//the processing of leave request is based on the parent form that generated this form
				postProcessLeaveRequest({ formId: parentFormId, statusId: 3 })
			).unwrap();
			//success notification
			notification.success({ message: "Leave processed successfully!" });
			//clear out previous results
			dispatch({ type: "RESET_ADMIN" });
			dispatch({ type: "RESET_CME" });
			//redirect to leave manager
			history.push("/CME/Coordinator/leavemanager");
		}
	};

	const adminError = useSelector(selectAdminError);
	useEffect(() => {
		if (adminError) {
			notification.error({
				message: "Error while process leave submission",
				description:
					"This leave form could not be processed correctly. Please try submitting again - if issues persist, please contact support.",
				duration: 0,
				onClose: () => dispatch(errorEdited(null)),
			});
		}
	}, [adminError, dispatch]);
	const formError = useSelector(selectFormError);
	useEffect(() => {
		if (formError) {
			notification.error({
				message: "Error while process leave submission",
				description:
					"This leave form could not be processed correctly. Please try submitting again - if issues persist, please contact support.",
				duration: 0,
				onClose: () => dispatch(formErrorEdited(null)),
			});
		}
	}, [formError, dispatch]);

	const adminStatus = useSelector(selectAdminStatus);
	const formStatus = useSelector(selectFormStatus);

	return (
		<Layout>
			<Header />
			<Content>
				{loading ? <GlobalLoading /> : <LeaveClaimContainer />}
			</Content>
			<Footer>
				<CustomButton
					visible={statusId === 1}
					type="primary"
					onClick={onSubmit}
					disabled={
						adminStatus === "loading" || formStatus === "loading"
					}
				>
					Submit
				</CustomButton>
			</Footer>
		</Layout>
	);
};

const LeaveClaimContainer = () => {
	const dispatch = useDispatch();

	//update screen name
	useEffect(() => {
		dispatch(screenTitleEdited("Edit Leave Taken"));
	}, [dispatch]);

	return <Leave type="leaveClaim" />;
};

export default LeaveClaim;
